import { cn } from "@/lib/helpers/cn";
import FileIcon from "@/assets/images/icons2/icon--file.svg?component";
import { FileTrigger2 } from "@/ui2/molecules/FileTrigger";
import { getAutomationDataAttr } from "@/utils/automationUtils";

import { ALLOWED_EXTENSIONS, ALLOWED_MIME } from "../constants";

type PhotoUploaderProps = {
  hasFile: boolean;
  fileName: string;
  fileStateSetter: (file: File[]) => void;
  isLoading: boolean;
  isValid: boolean;
};

export function PhotoUploader({
  hasFile,
  fileName,
  fileStateSetter,
  isLoading,
  isValid,
}: PhotoUploaderProps) {
  const isPrimaryPickerShown = !hasFile || !isValid || isLoading;
  const isSecondaryPickerShown = hasFile && isValid && !isLoading;

  return (
    <>
      {isPrimaryPickerShown && (
        <>
          <p className="body-md tw:flex tw:flex-col tw:text-center">
            <b>Upload front of your identity document</b>
            (driver's license, state ID, or passport)
          </p>

          <div className="tw:mx-auto tw:flex tw:max-w-60 tw:flex-col tw:items-center tw:gap-6">
            <FileTrigger2
              acceptedFileTypes={ALLOWED_MIME}
              buttonProps={{
                children: "Upload ID",
                className: "tw:min-w-45",
                isLoading,
                labelLoading: "Uploading...",
                size: "lg",
                ...getAutomationDataAttr("photo-id-upload-button"),
              }}
              fileStateSetter={fileStateSetter}
            />

            <small className="body-sm tw:text-center tw:text-text-secondary">
              {ALLOWED_EXTENSIONS} allowed.
              <br />
              Max file size: 20MB
            </small>
          </div>
        </>
      )}

      {isSecondaryPickerShown && (
        <div className="tw:grid tw:grid-cols-[1fr_auto] tw:items-center tw:gap-5">
          <p
            className={cn(
              "body-md tw:flex tw:gap-1.5",
              "tw:min-w-0", // important for truncate to work
            )}
          >
            <FileIcon className="tw:-mt-0.5 tw:shrink-0 tw:text-text-secondary" />

            <span
              className="tw:truncate"
              title={fileName}
              {...getAutomationDataAttr("photo-id-file-name")}
            >
              {fileName}
            </span>
          </p>

          <FileTrigger2
            acceptedFileTypes={ALLOWED_MIME}
            buttonProps={{
              children: "Re-upload",
              isDisabled: isLoading,
              size: "sm",
              variant: "secondary",
              ...getAutomationDataAttr("photo-id-reupload-button"),
            }}
            fileStateSetter={fileStateSetter}
          />
        </div>
      )}
    </>
  );
}
